import { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useMemoryStore } from "../features/memories/model/memoryStore";
import { SceneLoading } from "../shared/ui/SceneLoading";
import { isSupabaseConfigured } from "../shared/supabase/client";
import { useSpaceStore } from "../features/spaces/model/spaceStore";
import { AuthGate, SpaceGate } from "../features/spaces/SpaceGate";
import { supabase } from "../shared/supabase/client";
import { applyRuntimeDocumentState, probeRuntime, type RuntimeProbe } from "../shared/platform/runtime";
import { registerRuntimeDevice } from "../features/devices/data/deviceRegistrationRepository";
import { synchronisePendingEncryptedMedia } from "../features/devices/data/encryptedMediaRelayRepository";
import { scheduleV2DepthGeneration } from "../features/devices/data/v2DepthGenerationService";
import { useEncryptedSyncStatus } from "../features/devices/model/syncStatus";
import { syncFailureKindFromError } from "../features/devices/model/syncFailure";
import { PwaUpdatePrompt } from "../shared/pwa/PwaUpdatePrompt";

const TimelinePage = lazy(() =>
  import("../features/timeline/TimelinePage").then((module) => ({ default: module.TimelinePage }))
);
const MemoryEditorPage = lazy(() =>
  import("../features/memories/MemoryEditorPage").then((module) => ({ default: module.MemoryEditorPage }))
);
const MemoryDetailPage = lazy(() =>
  import("../features/immersive/MemoryDetailPage").then((module) => ({ default: module.MemoryDetailPage }))
);
const AuthPage = lazy(() => import("../features/spaces/AuthPage").then((module) => ({ default: module.AuthPage })));
const SpaceSetupPage = lazy(() => import("../features/spaces/SpaceSetupPage").then((module) => ({ default: module.SpaceSetupPage })));
const InvitePage = lazy(() => import("../features/spaces/InvitePage").then((module) => ({ default: module.InvitePage })));
const GalaxyManagementPage = lazy(() =>
  import("../features/spaces/GalaxyManagementPage").then((module) => ({ default: module.GalaxyManagementPage }))
);
const SettingsPage = lazy(() => import("../features/settings/SettingsPage").then((module) => ({ default: module.SettingsPage })));
const RecentlyDeletedPage = lazy(() =>
  import("../features/memories/RecentlyDeletedPage").then((module) => ({ default: module.RecentlyDeletedPage }))
);
const UniverseOverviewPage = lazy(() =>
  import("../features/universe/UniverseOverviewPage").then((module) => ({ default: module.UniverseOverviewPage }))
);
const GalaxyGate = lazy(() => import("../features/universe/GalaxyGate").then((module) => ({ default: module.GalaxyGate })));
const ENCRYPTED_SYNC_EVENT = "memory-universe:encrypted-sync";

function UniverseEntry() {
  const location = useLocation();
  const passwordRecovery = useSpaceStore((state) => state.passwordRecovery);
  const recoveryFromLink = new URLSearchParams(location.hash.slice(1)).get("type") === "recovery";
  if (passwordRecovery || recoveryFromLink) {
    return <Navigate to={`/auth?mode=recovery${location.hash}`} replace />;
  }
  return <AuthGate><UniverseOverviewPage /></AuthGate>;
}

export function App() {
  const loadMemories = useMemoryStore((state) => state.loadMemories);
  const memories = useMemoryStore((state) => state.memories);
  const initialize = useSpaceStore((state) => state.initialize);
  const initialized = useSpaceStore((state) => state.initialized);
  const space = useSpaceStore((state) => state.space);
  const user = useSpaceStore((state) => state.user);
  const refreshSpace = useSpaceStore((state) => state.refreshSpace);
  const setSyncing = useEncryptedSyncStatus((state) => state.setSyncing);
  const setSyncResult = useEncryptedSyncStatus((state) => state.setResult);
  const setSyncAttention = useEncryptedSyncStatus((state) => state.setAttention);
  const resetSyncStatus = useEncryptedSyncStatus((state) => state.reset);
  const [runtime, setRuntime] = useState<RuntimeProbe | null>(null);

  useEffect(() => {
    void probeRuntime().then((nextRuntime) => {
      applyRuntimeDocumentState(nextRuntime);
      setRuntime(nextRuntime);
    });
  }, []);

  useEffect(() => {
    if (!user || !runtime) {
      resetSyncStatus();
      return;
    }
    let active = true;
    let inFlight = false;
    const synchronise = async () => {
      if (inFlight) return;
      inFlight = true;
      setSyncing();
      try {
        await registerRuntimeDevice(user.id, runtime);
        const result = await synchronisePendingEncryptedMedia(runtime);
        setSyncResult(result);
        const changed = result.changed;
        if (changed && active) void loadMemories();
      } catch (error) {
        if (active) setSyncAttention(syncFailureKindFromError(error));
      } finally {
        inFlight = false;
      }
    };
    const retry = () => void synchronise();
    retry();
    const timer = window.setInterval(retry, 30_000);
    window.addEventListener("online", retry);
    window.addEventListener(ENCRYPTED_SYNC_EVENT, retry);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener("online", retry);
      window.removeEventListener(ENCRYPTED_SYNC_EVENT, retry);
    };
  }, [loadMemories, resetSyncStatus, runtime, setSyncAttention, setSyncResult, setSyncing, user]);

  useEffect(() => {
    let cleanup: () => void = () => undefined;
    void initialize().then((nextCleanup) => { cleanup = nextCleanup; });
    return () => cleanup();
  }, [initialize]);

  useEffect(() => {
    if (initialized && (!isSupabaseConfigured || space)) void loadMemories();
  }, [initialized, loadMemories, space]);

  useEffect(() => {
    if (!runtime || !space) return;
    for (const item of memories) {
      for (const media of item.media) {
        scheduleV2DepthGeneration({ galaxyId: item.memory.spaceId, mediaId: media.id, runtime });
      }
    }
  }, [memories, runtime, space]);

  useEffect(() => {
    if (!supabase || !space) return;
    const realtimeClient = supabase;
    const refresh = () => {
      void loadMemories();
      window.dispatchEvent(new Event(ENCRYPTED_SYNC_EVENT));
    };
    const refreshMembers = () => void refreshSpace();
    const channel = realtimeClient
      .channel(`memory-space-${space.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "memories", filter: `space_id=eq.${space.id}` }, refresh)
      .on("postgres_changes", { event: "*", schema: "public", table: "media_assets", filter: `space_id=eq.${space.id}` }, refresh)
      .on("postgres_changes", { event: "*", schema: "public", table: "media_transfer_jobs" }, refresh)
      .on("postgres_changes", { event: "*", schema: "public", table: "memberships", filter: `space_id=eq.${space.id}` }, refreshMembers)
      .subscribe();
    return () => { void realtimeClient.removeChannel(channel); };
  }, [loadMemories, refreshSpace, space]);

  return (
    <Suspense fallback={<SceneLoading />}>
      <PwaUpdatePrompt />
      <Routes>
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/join" element={<SpaceSetupPage />} />
        <Route path="/space/setup" element={<SpaceSetupPage />} />
        <Route path="/space/invite" element={<SpaceGate><InvitePage /></SpaceGate>} />
        <Route path="/" element={<UniverseEntry />} />
        <Route path="/galaxies/:galaxyId" element={<AuthGate><GalaxyGate><TimelinePage /></GalaxyGate></AuthGate>} />
        <Route path="/galaxies/:galaxyId/manage" element={<AuthGate><GalaxyGate><GalaxyManagementPage /></GalaxyGate></AuthGate>} />
        <Route path="/galaxies/:galaxyId/recently-deleted" element={<AuthGate><GalaxyGate><RecentlyDeletedPage /></GalaxyGate></AuthGate>} />
        <Route path="/galaxies/:galaxyId/memories/new" element={<AuthGate><GalaxyGate><MemoryEditorPage /></GalaxyGate></AuthGate>} />
        <Route path="/memories/new" element={<SpaceGate><MemoryEditorPage /></SpaceGate>} />
        <Route path="/memories/:memoryId" element={<SpaceGate><MemoryDetailPage /></SpaceGate>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
